import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Stack,
  Flex,
  Button,
  RadioGroup,
  Radio,
  Input,
  Select,
  Checkbox,
  CheckboxGroup,
  HStack,
  VStack,
  NumberInput,
  NumberInputField,
  Divider,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { FaStar, FaRegStar, FaUserSecret, FaUser } from "react-icons/fa";
import dayjs from "dayjs";
import axiosInstance from "../../../routes/axiosInstance";

const FormScreen = ({ space, admin, member, referenceId, fetchSpace }) => {
  const [form, setForm] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [existingResponse, setExistingResponse] = useState(null);
  const toast = useToast();

  const formSchema = space.metaData?.formSchema || [];

  // Check if user has already submitted a form response
  useEffect(() => {
    const checkExistingSubmission = async () => {
      if (!referenceId || !member) return;

      try {
        const config = {
          headers: {
            "Content-type": "application/json",
            Authorization: member?.guestAccessId
              ? `Guest ${member.guestAccessId}`
              : undefined,
          },
        };

        const response = await axiosInstance.get(
          `/api/spaces/${referenceId}/form-response`,
          config,
        );

        if (response.data.success) {
          setAlreadySubmitted(true);
          setExistingResponse(response.data.data);
          setForm(response.data.data.responses || {});
        }
      } catch (error) {
        // 404 means no form response exists yet, which is expected
        if (error.response?.status !== 404) {
          console.error("Error checking existing form response:", error);
        }
      }
    };

    checkExistingSubmission();
  }, [referenceId, member]);

  // Sort fields by order
  const sortedFields = formSchema.sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  const handleChange = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    sortedFields.forEach((field) => {
      if (field.required) {
        const value = form[field.fieldKey];

        // Check if field is empty or invalid
        if (
          !value ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "string" && value.trim() === "") ||
          (field.fieldType === "ratings" && (!value || value === 0))
        ) {
          newErrors[field.fieldKey] = `${field.label} is required`;
          hasErrors = true;
        }

        // Additional validation for email fields
        if (field.fieldType === "email" && value && value.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            newErrors[field.fieldKey] = "Please enter a valid email address";
            hasErrors = true;
          }
        }
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (alreadySubmitted) {
      toast({
        title: "Form already submitted",
        description:
          "You have already submitted this form. Multiple submissions are not allowed.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!validateForm()) {
      toast({
        title: "Please fill in all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: member?.guestAccessId
            ? `Guest ${member.guestAccessId}`
            : undefined,
        },
      };

      const response = await axiosInstance.post(
        `/api/spaces/${referenceId}/form-response`,
        {
          formResponse: form,
        },
        config,
      );

      if (response.data.success) {
        setSubmitted(true);
        setAlreadySubmitted(true);
        setExistingResponse(response.data.data);

        toast({
          title: "Form submitted successfully!",
          description: "Thank you for your response.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Refresh space data if fetchSpace function is available
        if (typeof fetchSpace === "function") {
          fetchSpace();
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);

      if (error.response?.status === 409) {
        // User already submitted
        setAlreadySubmitted(true);
        toast({
          title: "Form already submitted",
          description: "You have already submitted this form previously.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to submit form",
          description: error.response?.data?.error || "Please try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const { fieldType, fieldKey, placeholder, required, choices, scale } =
      field;
    const hasError = errors[fieldKey];

    switch (fieldType) {
      case "text":
        return (
          <Input
            placeholder={placeholder}
            value={form[fieldKey] || ""}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            isRequired={required}
            isInvalid={hasError}
            borderColor={hasError ? "red.500" : undefined}
            isDisabled={alreadySubmitted}
          />
        );

      case "number":
        return (
          <NumberInput
            value={form[fieldKey] || ""}
            onChange={(val) => handleChange(fieldKey, val)}
            isRequired={required}
            isInvalid={hasError}
            isDisabled={alreadySubmitted}
          >
            <NumberInputField
              placeholder={placeholder}
              borderColor={hasError ? "red.500" : undefined}
            />
          </NumberInput>
        );

      case "email":
        return (
          <Input
            type="email"
            placeholder={placeholder}
            value={form[fieldKey] || ""}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            isRequired={required}
            isInvalid={hasError}
            borderColor={hasError ? "red.500" : undefined}
            isDisabled={alreadySubmitted}
          />
        );

      case "select":
        return (
          <Select
            placeholder="Select an option"
            value={form[fieldKey] || ""}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            isRequired={required}
            isInvalid={hasError}
            borderColor={hasError ? "red.500" : undefined}
            isDisabled={alreadySubmitted}
          >
            {choices?.map((choice, ci) => (
              <option key={choice.id || ci} value={choice.id || choice}>
                {choice.label || choice}
              </option>
            ))}
          </Select>
        );

      case "radio":
        return (
          <RadioGroup
            onChange={(val) => handleChange(fieldKey, val)}
            value={form[fieldKey] || ""}
            isDisabled={alreadySubmitted}
          >
            <VStack align="start">
              {choices?.map((choice, ci) => (
                <Radio
                  key={choice.id || ci}
                  value={choice.id || choice}
                  colorScheme={hasError ? "red" : "teal"}
                  isDisabled={alreadySubmitted}
                >
                  {choice.label || choice}
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        );

      case "checkbox":
        return (
          <CheckboxGroup
            value={form[fieldKey] || []}
            onChange={(val) => handleChange(fieldKey, val)}
            colorScheme={hasError ? "red" : "teal"}
            isDisabled={alreadySubmitted}
          >
            <VStack align="start">
              {choices?.map((choice, ci) => (
                <Checkbox
                  key={choice.id || ci}
                  value={choice.id || choice}
                  isDisabled={alreadySubmitted}
                >
                  {choice.label || choice}
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        );

      case "ratings":
        const ratingScale = scale || 5;
        const currentRating = form[fieldKey] || 0;

        return (
          <HStack spacing={1}>
            {Array.from({ length: ratingScale }, (_, i) => i + 1).map(
              (rating) => (
                <Icon
                  key={rating}
                  as={rating <= currentRating ? FaStar : FaRegStar}
                  color={
                    hasError
                      ? "red.400"
                      : rating <= currentRating
                        ? "yellow.400"
                        : "gray.300"
                  }
                  cursor={alreadySubmitted ? "default" : "pointer"}
                  size="lg"
                  onClick={() =>
                    !alreadySubmitted && handleChange(fieldKey, rating)
                  }
                  _hover={{
                    color: hasError ? "red.500" : "yellow.400",
                    transform: alreadySubmitted ? "none" : "scale(1.1)",
                  }}
                  transition="all 0.2s"
                />
              ),
            )}
            <Text
              ml={2}
              fontSize="sm"
              color={hasError ? "red.500" : "gray.600"}
            >
              {currentRating > 0
                ? `${currentRating}/${ratingScale}`
                : "Not rated"}
            </Text>
          </HStack>
        );

      default:
        return (
          <Input
            value={form[fieldKey] || ""}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            isRequired={required}
            isInvalid={hasError}
            borderColor={hasError ? "red.500" : undefined}
            isDisabled={alreadySubmitted}
          />
        );
    }
  };

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-br, teal.600, white, teal.50)"
    >
      {/* Content area */}
      <Flex direction="column" align="center" flex="1" w="100%" p={4}>
        {/* Header section */}
        <Box
          w="100%"
          maxW="lg"
          p={4}
          mt={4}
          bg="white"
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
        >
          {/* Form title and description */}
          <Box textAlign="left">
            <Text fontWeight="bold" fontSize="2xl" mb={2}>
              {space?.title || "Form"}
            </Text>
            <Text fontSize="sm" color="gray.600" pb={2}>
              {space.metaData?.description || "Please fill out this form"}
            </Text>
            <Divider />
          </Box>

          {/* Member info after divider */}
          <Flex justify="flex-start" mt={4}>
            <Flex align="center">
              <Icon as={FaUser} color="gray.500" mr={2} />
              <Text
                fontWeight="medium"
                fontSize="sm"
                color="gray.700"
                noOfLines={1}
              >
                {member?.name || member?.alias || "You"}
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* Form container */}
        <Box
          p={8}
          mt={2}
          maxW="lg"
          w="100%"
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="md"
        >
          {alreadySubmitted ? (
            <Box textAlign="center" py={8}>
              <Icon as={FaUser} color="green.500" mb={4} boxSize={12} />
              <Text color="green.600" fontSize="xl" fontWeight="bold" mb={2}>
                Form Submitted successfully.
              </Text>
              <Text color="gray.600" mb={4}>
                Thank you for your response! You submitted this form on{" "}
                {existingResponse?.submittedAt
                  ? dayjs(existingResponse.submittedAt).format(
                      "MMM D, YYYY h:mm A",
                    )
                  : "a previous date"}
                .
              </Text>
            </Box>
          ) : submitted ? (
            <Box textAlign="center" py={8}>
              <Icon as={FaUser} color="green.500" mb={4} boxSize={12} />
              <Text color="green.600" fontSize="xl" fontWeight="bold" mb={2}>
                Thank you for your response!
              </Text>
              <Text color="gray.600">
                Your form has been submitted successfully.
              </Text>
            </Box>
          ) : (
            <Stack spacing={4}>
              {sortedFields.map((field, idx) => (
                <Box
                  key={field.fieldKey || idx}
                  p={4}
                  borderWidth="1px"
                  borderColor={errors[field.fieldKey] ? "red.300" : "gray.200"}
                  borderRadius="md"
                  bg={errors[field.fieldKey] ? "red.50" : "gray.25"}
                  _hover={{
                    borderColor: errors[field.fieldKey]
                      ? "red.400"
                      : "teal.300",
                    bg: errors[field.fieldKey] ? "red.50" : "gray.25",
                  }}
                  transition="all 0.2s"
                >
                  <Text fontWeight="bold" mb={3} color="gray.700">
                    {field.label}
                    {field.required && (
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    )}
                  </Text>
                  {renderField(field)}
                  {errors[field.fieldKey] && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {errors[field.fieldKey]}
                    </Text>
                  )}
                </Box>
              ))}
              <Button
                colorScheme="teal"
                onClick={handleSubmit}
                w="full"
                mt={6}
                isLoading={isSubmitting}
                loadingText="Submitting..."
              >
                Submit
              </Button>
            </Stack>
          )}
        </Box>

        {/* Admin info at the bottom */}
        <Box mt={4} textAlign="center">
          <Text
            fontSize="sm"
            color="gray.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            Created by {admin?.name || admin?.alias || "Unknown User"}
            {space.isIncognito ? (
              <FaUserSecret style={{ marginLeft: 8 }} title="Incognito" />
            ) : (
              <FaUser style={{ marginLeft: 8 }} title="Admin" />
            )}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {space.createdAt
              ? dayjs(space.createdAt).format("MMM D, YYYY h:mm A")
              : ""}
          </Text>
        </Box>
      </Flex>

      {/* Footer - Always at bottom */}
      <Box w="100%" textAlign="center" py={4}>
        <Text fontSize="xs" color="gray.500" mb={2}>
          This content is neither created nor endorsed by Vartaverse
        </Text>
        <Text
          fontSize="lg"
          fontWeight="bold"
          color="teal.600"
          cursor="pointer"
          _hover={{
            color: "teal.700",
            textDecoration: "none",
          }}
          onClick={() => window.open(window.location.origin, "_blank")}
        >
          VartaVerse
        </Text>
      </Box>
    </Flex>
  );
};

export default FormScreen;
